import React, { useEffect, useRef, useState } from 'react';
import { Eye, MonitorUp, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  GlanceSession,
  ICE_SERVERS,
  destroyGlanceSession,
  pushIceCandidate,
  setGlanceAnswer,
  setGlanceOffer,
  setGlanceStatus,
  subscribeGlanceSession,
  subscribeIceCandidates,
  subscribeIncomingGlance,
} from '../../services/glanceService';

/**
 * Mounted globally for every signed-in user. Listens for inbound Glance
 * requests where `targetUid` is this user. Browsers gate
 * `getDisplayMedia()` behind a user gesture, so we surface a small
 * consent prompt that doubles as the gesture — clicking "Share Screen"
 * fires the screen-picker dialog and starts publishing video to the
 * viewer over WebRTC.
 */
export const GlanceTargetListener: React.FC = () => {
  const { currentUser } = useAuth();

  // Pending inbound request awaiting user's click.
  const [pending, setPending] = useState<GlanceSession | null>(null);
  // Active session being driven on the target side.
  const [streaming, setStreaming] = useState<{
    sessionId: string;
    viewerName: string;
    stream: MediaStream;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const sessionUnsubRef = useRef<(() => void) | null>(null);
  const candUnsubRef = useRef<(() => void) | null>(null);
  // Sessions we've already shown a prompt for, so a snapshot re-emit
  // doesn't re-prompt for the same request id.
  const seenIdsRef = useRef<Set<string>>(new Set());

  // ---------------- inbound request listener ----------------

  useEffect(() => {
    if (!currentUser) return;
    const unsub = subscribeIncomingGlance(currentUser.uid, (session) => {
      if (session.status !== 'requesting') return;
      if (seenIdsRef.current.has(session.id)) return;
      seenIdsRef.current.add(session.id);
      // Show the prompt; if one is already up, stash the new one until
      // the current is resolved.
      setPending((cur) => cur || session);
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const declineRequest = async () => {
    if (!pending) return;
    const id = pending.id;
    setPending(null);
    try {
      await setGlanceStatus(id, 'declined', 'User declined');
    } catch (err) {
      console.warn('declineRequest failed', err);
    }
  };

  const acceptRequest = async () => {
    if (!pending) return;
    const session = pending;
    setPending(null);
    await startStreaming(session);
  };

  const startStreaming = async (session: GlanceSession) => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15, max: 24 } },
        audio: false,
      });

      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      pcRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      stream.getVideoTracks()[0]?.addEventListener('ended', () => {
        endStreaming(session.id);
      });

      pc.onicecandidate = (e) => {
        if (e.candidate) {
          pushIceCandidate(session.id, 'target', e.candidate).catch((err) => {
            console.warn('pushIceCandidate target failed', err);
          });
        }
      };
      pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === 'failed' ||
          pc.connectionState === 'disconnected' ||
          pc.connectionState === 'closed'
        ) {
          endStreaming(session.id);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await setGlanceOffer(session.id, offer);
      await setGlanceStatus(session.id, 'accepted');

      sessionUnsubRef.current = subscribeGlanceSession(session.id, async (s) => {
        if (!s) {
          endStreaming(session.id);
          return;
        }
        if (s.answer && pc.signalingState !== 'stable') {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(s.answer));
            await setGlanceStatus(session.id, 'streaming');
          } catch (err) {
            console.warn('setRemoteDescription target failed', err);
          }
        }
        if (s.status === 'ended' || s.status === 'declined') {
          endStreaming(session.id);
        }
      });

      candUnsubRef.current = subscribeIceCandidates(session.id, 'viewer', async (c) => {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(c));
        } catch (err) {
          console.warn('addIceCandidate target failed', err);
        }
      });

      setStreaming({
        sessionId: session.id,
        viewerName: session.viewerName,
        stream,
      });
    } catch (err: any) {
      console.error('Failed to start glance', err);
      const reason =
        err?.name === 'NotAllowedError'
          ? 'User cancelled the screen-picker'
          : err?.message || 'unknown error';
      setError(reason);
      try {
        await setGlanceStatus(session.id, 'declined', reason);
      } catch {
        /* swallow */
      }
      cleanupConnection();
    }
  };

  const cleanupConnection = () => {
    sessionUnsubRef.current?.();
    candUnsubRef.current?.();
    sessionUnsubRef.current = null;
    candUnsubRef.current = null;
    if (pcRef.current) {
      pcRef.current.getSenders().forEach((s) => {
        try {
          s.track?.stop();
        } catch {
          /* swallow */
        }
      });
      try {
        pcRef.current.close();
      } catch {
        /* swallow */
      }
      pcRef.current = null;
    }
  };

  const endStreaming = async (sessionId: string) => {
    try {
      streaming?.stream.getTracks().forEach((t) => t.stop());
    } catch {
      /* swallow */
    }
    cleanupConnection();
    setStreaming(null);
    try {
      await destroyGlanceSession(sessionId);
    } catch {
      /* swallow */
    }
  };

  // ---------------- UI ----------------

  return (
    <>
      {/* Consent prompt — also serves as the user gesture required by
          the browser to allow getDisplayMedia(). */}
      {pending && (
        <div className="fixed inset-0 z-[210] bg-black/60 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-100 px-5 py-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center">
                <Eye className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-[14px] font-semibold text-gray-900">Glance request</h2>
                <p className="text-[11px] text-gray-600">From {pending.viewerName}</p>
              </div>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[13px] text-gray-700 leading-relaxed">
                <strong>{pending.viewerName}</strong> is asking to view your screen.
                Click "Share Screen" and pick a window or tab to start.
              </p>
              <p className="text-[11px] text-gray-500">
                You can stop sharing anytime via the browser's "Stop sharing" bar
                or the indicator in the corner of this app.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
              <button
                type="button"
                onClick={declineRequest}
                className="px-3 py-2 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={acceptRequest}
                className="px-3 py-2 text-[12px] font-medium bg-[#1A1A1A] text-white rounded-md hover:bg-[#2a2a2a] flex items-center justify-center gap-1.5"
              >
                <MonitorUp className="w-3.5 h-3.5" />
                Share Screen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Persistent indicator while streaming */}
      {streaming && (
        <div className="fixed bottom-4 left-4 z-[150] bg-red-600 text-white rounded-full shadow-lg flex items-center gap-2 pl-3 pr-1.5 py-1.5">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span className="text-[11px] font-semibold">
            Sharing screen with {streaming.viewerName}
          </span>
          <button
            type="button"
            onClick={() => endStreaming(streaming.sessionId)}
            title="Stop sharing"
            className="ml-1 w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 left-4 z-[150] bg-gray-900 text-white text-[11px] rounded-md px-3 py-1.5 shadow-lg">
          Glance failed: {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            dismiss
          </button>
        </div>
      )}
    </>
  );
};

export default GlanceTargetListener;
